"use client";

import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";

const CARD_BG = "color-mix(in srgb, var(--surface-1) 45%, var(--background))";
const CARD_BORDER = "color-mix(in srgb, var(--foreground) 8%, transparent)";

// Curated labels for known keys — anything else falls back to an auto-generated label below.
const KEY_LABEL: Record<string, string> = {
  web_scraping: "Web Scraping",
  price_monitoring: "Price Monitoring",
  seo_monitoring: "SEO Monitoring",
  ai_llm_training: "AI & LLM Training",
  market_research: "Market Research",
  brand_protection: "Brand Protection",
  ai_agents: "AI Agents",
  ad_verification: "Ad Verification",
  geolocation_testing: "Geolocation Testing",
};

// Curated accents for known keys — anything else gets a deterministic color from ACCENT_PALETTE below.
const KEY_ACCENT: Record<string, string> = {
  web_scraping: "#5b8cff",
  price_monitoring: "#22d3ee",
  seo_monitoring: "#60A5FA",
  ai_llm_training: "#A78BFA",
  market_research: "#22D3EE",
  brand_protection: "#FB7185",
  ai_agents: "#FB923C",
  ad_verification: "#34D399",
  geolocation_testing: "#F472B6",
};

// Extra colors assigned to any use case key that isn't in KEY_ACCENT, so new use cases
// always get a distinct tag color without a code change.
const ACCENT_PALETTE = [
  "#FB923C",
  "#34D399",
  "#F472B6",
  "#38BDF8",
  "#FACC15",
  "#C084FC",
  "#4ADE80",
  "#F87171",
];

const FALLBACK_ACCENT = "#5b8cff";

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function accentForKey(key: string): string {
  return ACCENT_PALETTE[hashString(key) % ACCENT_PALETTE.length];
}

function humanizeKey(key: string): string {
  return key
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export interface UseCaseCardItem {
  uid: string;
  title: string;
  description: string;
  imageUrl: string | null;
  useCaseKey: string | null;
}

interface Props {
  cards: UseCaseCardItem[];
}

export default function UseCaseCards({ cards }: Props) {
  const reduced = useReducedMotion() ?? false;
  const router = useRouter();

  if (cards.length === 0) {
    return (
      <section className="border-border border-b py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-muted-foreground text-center text-sm">No use cases published yet.</p>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="use-cases-grid-heading"
      className="border-border border-b pt-[--spacing-section] pb-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mt-4 mb-12 max-w-xl">
          <h2
            id="use-cases-grid-heading"
            className="text-foreground mb-3 scroll-mt-40 text-3xl font-bold tracking-tight sm:text-4xl"
          >
            All Proxy Use Cases
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            Nine real-world workflows where proxy infrastructure changes what&apos;s possible.
          </p>
        </div>

        <ul className="grid list-none grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card, i) => {
            const key = card.useCaseKey ?? "";
            const accent = KEY_ACCENT[key] ?? (key ? accentForKey(key) : FALLBACK_ACCENT);
            const label = KEY_LABEL[key] ?? (key ? humanizeKey(key) : null);
            return (
              <motion.li
                key={card.uid}
                initial={reduced ? false : { opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={scrollRevealViewport}
                transition={{ duration: 0.4, delay: Math.min(i, 5) * 0.06 }}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => router.push(`/use-cases/${card.uid}`)}
                  onKeyDown={(e) => e.key === "Enter" && router.push(`/use-cases/${card.uid}`)}
                  className="group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl transition-all duration-300"
                  style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}
                >
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                    style={{
                      background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${accent}18, transparent 70%)`,
                    }}
                  />

                  <div
                    className="relative overflow-hidden"
                    style={{
                      aspectRatio: "300 / 157",
                      background: CARD_BG,
                      borderBottom: `1px solid ${CARD_BORDER}`,
                    }}
                  >
                    {card.imageUrl ? (
                      <Image
                        src={card.imageUrl}
                        alt={card.title}
                        fill
                        sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                        className="object-cover"
                        style={{ filter: "hue-rotate(-70deg) saturate(0.5) brightness(1.1)" }}
                        priority={i === 0}
                        loading={i === 0 ? undefined : "lazy"}
                      />
                    ) : (
                      <div
                        aria-hidden="true"
                        className="absolute inset-0 opacity-60"
                        style={{
                          background: `radial-gradient(ellipse at top left, ${accent}26, transparent 70%)`,
                        }}
                      />
                    )}
                  </div>

                  <div className="relative flex flex-1 flex-col p-5">
                    {label && (
                      <span
                        className="light:text-[color-mix(in_srgb,var(--chip-accent)_55%,black)] mb-2 inline-block w-fit rounded-md px-2 py-0.5 text-[11px] font-semibold text-(--chip-accent)"
                        style={
                          {
                            background: `${accent}18`,
                            "--chip-accent": accent,
                          } as React.CSSProperties
                        }
                      >
                        {label}
                      </span>
                    )}
                    <h3 className="text-foreground mb-2 text-base leading-snug font-semibold transition-colors">
                      {card.title}
                    </h3>
                    <p className="text-muted-foreground flex-1 text-sm leading-relaxed">
                      {card.description}
                    </p>
                    <div className="mt-4 flex justify-end">
                      <Link
                        href={`/use-cases/${card.uid}`}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-primary text-primary-foreground inline-flex items-center rounded-lg px-4 py-2 text-xs font-semibold transition-opacity hover:opacity-90"
                      >
                        Read the full guide →
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
